/**
 * One name per Open Home action, shared by the Axiom API logs and the GTM/GA4 events, so a funnel
 * reads the same in both (Thomas, 10-27: "keep full track of the service"). The API is one route
 * with the resource path in `?p=`; its segments are positional, so ids are found by position, never
 * guessed from their shape.
 */

/** `rooms/<uuid>/photos/<id>/blur` becomes `rooms/:id/photos/:id/blur`. */
export const routeOf = (parts: string[]) => {
  const [resource, id, sub, subId, action] = parts;

  return [
    resource,
    id && ":id",
    // `image/<room>/<photo>`: the third segment is an id too.
    sub && (resource === "image" ? ":id" : sub),
    subId && ":id",
    action,
  ]
    .filter(Boolean)
    .join("/");
};

/** The path the client passes to `openHomeFetch` (`rooms/<id>?view=x`), as its route. */
export const routeOfPath = (path: string) =>
  routeOf((path.split("?")[0] ?? "").split("/").filter(Boolean));

const EVENTS: Record<string, string> = {
  "GET search": "shop_browsed",
  "GET count": "teaser_counted",
  "GET rooms": "my_rooms_viewed",
  "GET rooms/:id": "room_viewed",
  "GET offers": "offers_viewed",
  "GET favorites": "favorites_viewed",
  "GET saved-rooms": "saved_rooms_viewed",
  "GET price-range/:id": "price_range_viewed",
  "POST rooms": "room_created",
  "PATCH rooms/:id": "room_updated",
  "DELETE rooms/:id": "room_deleted",
  "POST rooms/:id/photos": "photo_uploaded",
  "POST rooms/:id/photos/:id/blur": "photo_blurred",
  "POST rooms/:id/photos/:id/move": "photo_moved",
  "POST rooms/:id/photos/:id/replace": "photo_replace_checked",
  "POST rooms/:id/photos/:id/replace-confirm": "photo_replaced",
  "DELETE rooms/:id/photos/:id": "photo_removed",
  "POST rooms/:id/privacy-check": "privacy_checked",
  "PATCH rooms/:id/items/:id": "piece_updated",
  "POST offers": "offer_made",
  "POST offers/:id": "offer_answered",
  "POST favorites": "favorite_toggled",
  "POST saved-rooms": "room_save_toggled",
  "POST items/:id/ask": "owner_asked",
};

/**
 * A room photo the safety scan refused, on upload or replace (Thomas, 10-27): `photo_blocked` with
 * `oh_reason` (`PHOTO_REFUSAL_REASONS`) in the API log and as `oh_photo_blocked` in GA4, instead of
 * a failed upload. The action that was refused travels along as `oh_event` in GA4.
 */
export const PHOTO_BLOCKED_EVENT = "photo_blocked";

/** The action's name, or null for plumbing reads (`me`, `summary`, images, `status`). */
export const eventOf = (method: string, route: string) =>
  EVENTS[`${method.toUpperCase()} ${route}`] ?? null;

/** Field names of a change, never their values: which part of a piece or room the owner edited. */
export const changedKeys = (body: unknown) =>
  body && typeof body === "object" && !Array.isArray(body)
    ? Object.keys(body).sort().join(",").slice(0, 120)
    : null;
